'use client';

import { useMemo } from 'react';
import {
  insertParagraphSlotPlaceholders,
  normalizeArticleSlotId,
} from '../../../lib/paragraph-slot-placeholders';
import { renderArticleParagraphSlot } from './article-paragraph-slot-registry';

const SLOT_PLACEHOLDER_RE =
  /<div\b[^>]*\b(?:id|data-article-slot)=["']([^"']+)["'][^>]*\bclass=["'][^"']*\barticle-paragraph-slot\b[^"']*["'][^>]*>\s*<\/div>|<div\b[^>]*\bclass=["'][^"']*\barticle-paragraph-slot\b[^"']*["'][^>]*\b(?:id|data-article-slot)=["']([^"']+)["'][^>]*>\s*<\/div>/gi;

/**
 * Quebra o HTML nos placeholders e intercala componentes React (sem createPortal).
 * @param {string} html
 * @returns {Array<{ type: 'html', html: string } | { type: 'slot', id: string }>}
 */
function splitHtmlByArticleSlots(html) {
  if (!html) return [];

  /** @type {Array<{ type: 'html', html: string } | { type: 'slot', id: string }>} */
  const parts = [];
  let lastIndex = 0;
  let match;

  const re = new RegExp(SLOT_PLACEHOLDER_RE.source, 'gi');
  while ((match = re.exec(html)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'html', html: html.slice(lastIndex, match.index) });
    }
    const id = match[1] || match[2];
    if (id) {
      parts.push({ type: 'slot', id });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < html.length) {
    parts.push({ type: 'html', html: html.slice(lastIndex) });
  }

  return parts;
}

/**
 * Renderiza HTML do post e monta slots React após o N-ésimo `<p>`.
 */
export default function PostContentWithAds({
  html,
  paragraphSlots = [],
  className = 'content',
}) {
  const slotById = useMemo(() => {
    const map = new Map();
    paragraphSlots.forEach((slot, i) => {
      const id = normalizeArticleSlotId(slot.slotId, i);
      if (!slot.slotType) return;
      map.set(id, renderArticleParagraphSlot(slot.slotType, slot.slotProps || {}));
    });
    return map;
  }, [paragraphSlots]);

  const parts = useMemo(() => {
    if (!html) return [];
    if (!paragraphSlots.length) {
      return [{ type: 'html', html }];
    }

    const withPlaceholders = insertParagraphSlotPlaceholders(
      html,
      paragraphSlots.map((s, i) => ({
        afterParagraph: s.afterParagraph,
        slotId: normalizeArticleSlotId(s.slotId, i),
      }))
    );

    return splitHtmlByArticleSlots(withPlaceholders);
  }, [html, paragraphSlots]);

  if (!html) return null;

  return (
    <div className={className}>
      {parts.map((part, index) => {
        if (part.type === 'html') {
          if (!part.html) return null;
          return (
            <div
              key={`html-${index}`}
              className="post-content-chunk"
              dangerouslySetInnerHTML={{ __html: part.html }}
            />
          );
        }

        const node = slotById.get(part.id);
        return (
          <div
            key={part.id || `slot-${index}`}
            id={part.id}
            className="article-paragraph-slot"
            data-article-slot={part.id}
          >
            {node}
          </div>
        );
      })}
    </div>
  );
}
